import React from 'react';
import { useTranslation } from 'localization';
import './DeployReadinessChecklistMolecule.scss';

export interface ReadinessItem {
  id: string;
  label: string;
  description?: string;
}

export interface DeployReadinessChecklistMoleculeProps {
  requiredInputs: ReadinessItem[];
  optionalInputs: ReadinessItem[];
  dependencies: ReadinessItem[];
  className?: string;
  header?: React.ReactNode;
}

interface ChecklistGroupProps {
  titleKey: string;
  emptyKey: string;
  items: ReadinessItem[];
  iconClass: string;
  variant: 'required' | 'optional' | 'dependency';
}

const ChecklistGroup: React.FC<ChecklistGroupProps> = ({
  titleKey,
  emptyKey,
  items,
  iconClass,
  variant,
}) => {
  const { t } = useTranslation();
  const isEmpty = items.length === 0;
  return (
    <div
      className={`marketplace-deploy-readiness__group marketplace-deploy-readiness__group--${variant}`}
    >
      <header className="marketplace-deploy-readiness__group-title">{t(titleKey)}</header>
      {isEmpty ? (
        <p className="marketplace-deploy-readiness__empty">{t(emptyKey)}</p>
      ) : (
        <ul className="marketplace-deploy-readiness__list">
          {items.map((item) => (
            <li key={item.id} className="marketplace-deploy-readiness__row">
              <i
                className={`marketplace-deploy-readiness__icon pi ${iconClass}`}
                aria-hidden="true"
              />
              <div className="marketplace-deploy-readiness__row-body">
                <span className="marketplace-deploy-readiness__label">
                  {item.label}
                </span>
                {item.description && (
                  <span className="marketplace-deploy-readiness__description">
                    {item.description}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DeployReadinessChecklistMolecule: React.FC<
  DeployReadinessChecklistMoleculeProps
> = ({
  requiredInputs,
  optionalInputs,
  dependencies,
  className = '',
  header,
}) => {
  const { t } = useTranslation();
  const rootClasses = ['marketplace-deploy-readiness', className]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={rootClasses}>
      {header ?? (
        <header className="marketplace-deploy-readiness__header">
          {t('marketplace.approachDetail.overview.readiness.sectionTitle')}
        </header>
      )}
      <ChecklistGroup
        variant="required"
        iconClass="pi-asterisk"
        items={requiredInputs}
        titleKey="marketplace.approachDetail.overview.readiness.requiredInputs"
        emptyKey="marketplace.approachDetail.overview.readiness.requiredInputsEmpty"
      />
      <ChecklistGroup
        variant="optional"
        iconClass="pi-circle"
        items={optionalInputs}
        titleKey="marketplace.approachDetail.overview.readiness.optionalInputs"
        emptyKey="marketplace.approachDetail.overview.readiness.optionalInputsEmpty"
      />
      <ChecklistGroup
        variant="dependency"
        iconClass="pi-link"
        items={dependencies}
        titleKey="marketplace.approachDetail.overview.readiness.dependencies"
        emptyKey="marketplace.approachDetail.overview.readiness.dependenciesEmpty"
      />
    </section>
  );
};

DeployReadinessChecklistMolecule.displayName = 'DeployReadinessChecklistMolecule';

export default DeployReadinessChecklistMolecule;
